import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

// A Cognito access token lasts an hour, and the Authorization header is set once, at page
// load (AuthContext.checkAuth) - a page left open through a class outlives it, and routes
// that check who is asking (saving a profile, joining a session) would then answer 401.
// Within this long of its expiry, a request first has Amplify refresh the token.
const COGNITO_REFRESH_MARGIN_MS = 2 * 60 * 1000

// When the Cognito access token in an Authorization header expires (ms since 1970), or
// null for no header or another kind of token (our own, for guests and password logins).
export const cognitoTokenExpiry = (header) => {
  if (typeof header !== 'string' || !header.startsWith('Bearer ')) return null
  try {
    const part = header.slice('Bearer '.length).split('.')[1]
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')))
    if (payload.token_use !== 'access' || !String(payload.iss || '').includes('cognito-idp')) return null
    return Number(payload.exp) * 1000 || null
  } catch (error) {
    return null
  }
}

class AxiosManager {

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 10000,
    })

    this.axiosInstance.interceptors.request.use(async (config) => {
      const header = this.axiosInstance.defaults.headers.common['Authorization']
      const expires = cognitoTokenExpiry(header)
      // A request that set its own Authorization keeps it.
      const own = config.headers?.Authorization
      if (expires && expires - Date.now() < COGNITO_REFRESH_MARGIN_MS && (!own || own === header)) {
        const fresh = await this.refreshCognitoToken()
        if (fresh) config.headers = { ...config.headers, Authorization: `Bearer ${fresh}` }
      }
      return config
    })

    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('accessToken')
      if (token) {
        this.setAuthToken(token)

        this.verifyToken(token).catch(() => {
          localStorage.removeItem('accessToken')
          delete this.axiosInstance.defaults.headers.common['Authorization']
        })
      }
    }
  }

  async verifyToken (token) {
    // try {
    //   await this.axiosInstance.post('/auth/verify', { token })
    // } catch (error) {
    //   throw new Error('Invalid token')
    // }
  }

  static getInstance () {
    if (!AxiosManager.instance) {
      AxiosManager.instance = new AxiosManager()
    }
    return AxiosManager.instance
  }

  getAxiosInstance () {
    return this.axiosInstance
  }

  // One refresh at a time: requests sent meanwhile wait for it. Resolves to the new token,
  // or null when Amplify cannot refresh (signed out, refresh token expired) - the old
  // header then stays and the server's 401 stands. After a failure, requests in the next
  // minute do not try again, so a page that polls does not call Cognito on every poll.
  refreshCognitoToken () {
    if (this.refreshFailedAt && Date.now() - this.refreshFailedAt < 60 * 1000) return Promise.resolve(null)
    if (!this.refreshing) {
      const refreshed = this.axiosInstance.defaults.headers.common['Authorization']
      this.refreshing = fetchAuthSession({ forceRefresh: true })
        .then(({ tokens }) => tokens?.accessToken?.toString() || null)
        .catch(() => null)
        .then((token) => {
          this.refreshing = null
          // Signed out, or another account signed in, while this was in flight: their
          // header stays.
          if (this.axiosInstance.defaults.headers.common['Authorization'] !== refreshed) return null
          this.refreshFailedAt = token ? 0 : Date.now()
          if (token) this.setAuthToken(token)
          return token
        })
    }
    return this.refreshing
  }

  setAuthToken (token) {
    this.axiosInstance.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${token}`
    if (typeof window !== 'undefined') {
      localStorage.setItem('accessToken', token)
    }
  }


  clearAuthToken () {
    delete this.axiosInstance.defaults.headers.common['Authorization']
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken')
    }
  }
}


export const axiosInstance = AxiosManager.getInstance().getAxiosInstance()

export const axiosManager = AxiosManager.getInstance()